import React, { useEffect, useState } from 'react'

import classNames from 'classnames'
import queryString from 'query-string'
import { useTranslation } from 'react-i18next'
import { Link, Outlet, useLocation, useParams } from 'react-router-dom'
import { Slide, ToastContainer } from 'react-toastify'

import Embed from '@components/Embed'
import TitleDetails from '@components/music/title-details/TitleDetails'
import Breadcrumbs from '@components/shared/Breadcrumbs'
import LoadingSpinner from '@components/shared/LoadingSpinner'
import useWindowSize from '@hooks/useWindowSize'
import PageLayout from '@layout/PageLayout'
import Title from '@models/Title'
import { usePageApi } from '@providers/PageApiProvider'
import { SearchCategory } from '@services/SearchService'
import { useDialogStore } from '@stores/dialog.store'
import { isTabletLayout } from '@utils/mobile'
import PageRoutes from '@utils/page-routes'

import type { DetailPageApiResponse } from '@providers/types/api-responses'
import type { SearchConfig } from '@services/SearchService'

import 'react-toastify/dist/ReactToastify.min.css'

type MaybeQueryState = {
  query?: string
}

const searchConfig: SearchConfig = Object.freeze({
  [SearchCategory.TITLE]: 16,
})

const TitleDetailPage: React.FC = () => {
  const { width } = useWindowSize()
  const openDialog = useDialogStore((s) => s.openDialog)
  const { pageData } = usePageApi<DetailPageApiResponse>()
  const { t } = useTranslation()
  const params = useParams()

  const location = useLocation()

  const [isMobile, setIsMobile] = useState<boolean | null>(null)
  const [title, setTitle] = useState<Title | null>(
    pageData?.title ? new Title(pageData?.title) : null
  )
  const [showDetails, setShowDetails] = useState(true)

  useEffect(() => {
    const urlParams = queryString.parse(location.search, {
      arrayFormat: 'comma',
    })

    const openPurchaseMoreDialog = urlParams['openPurchaseMoreDialog']
    if (openPurchaseMoreDialog) {
      openDialog('purchase_title_dialog')
    }
  }, [])

  useEffect(() => {
    if (pageData?.title) setTitle(new Title(pageData.title))
  }, [pageData?.title])

  useEffect(() => {
    if (width) {
      const mobile = isTabletLayout(width)
      setIsMobile(mobile)
      if (mobile) setShowDetails(false)
    }
  }, [width])

  // If width hasn't been calculated yet, show loading spinner to prevent flyout from moving on mobile load
  if (!pageData || isMobile === null) {
    return (
      <PageLayout hideFooter searchConfig={searchConfig}>
        <LoadingSpinner />
      </PageLayout>
    )
  }
  if (pageData?.errors) return <Outlet context={{ searchConfig, errors: pageData.errors }} />

  return (
    <PageLayout
      title={pageData.page_title || (title ? `${title.title} | Noteflight` : 'Noteflight')}
      hideFooter
      searchConfig={searchConfig}>
      <div
        className="relative flex flex-row justify-end"
        data-testid={`title-details-${params.id}${params.slug ? `-${params.slug}` : ''}`}>
        <ToastContainer
          position="top-center"
          autoClose={1500}
          transition={Slide}
          closeButton={false}
          toastClassName="text-black text-md font-semibold w-[250px] h-[50px]"
          hideProgressBar
          newestOnTop
        />
        <div className="flex-1 grow">
          <div className="absolute top-0 left-0 p-5">
            <Breadcrumbs>
              <Link to={PageRoutes.musicPath()}>{t('titles.music')}</Link>
              <Link to={PageRoutes.musicSearchPath((location.state as MaybeQueryState)?.query)}>
                {t('titles.searchResults')}
              </Link>
            </Breadcrumbs>
          </div>
          <div className="flex flex-row items-center justify-center mt-24 mb-10">
            <Embed title={title} />
          </div>
        </div>
        {/* Flex spacer */}
        <div
          className={classNames(
            'hidden lg:block flex-none transition-width duration-300',
            showDetails ? 'w-[375px]' : 'w-0'
          )}
        />

        {/* Mobile backdrop */}
        <button
          type="button"
          className={classNames(
            'fixed inset-0 block lg:hidden bg-black bg-opacity-20',
            showDetails ? 'z-[1]' : 'z-[-1]',
            {
              hidden: !showDetails,
            }
          )}
          onClick={() => setShowDetails(false)}
        />

        <div
          data-testid="title-details-panel"
          className={classNames(
            'fixed h-full lg:h-[calc(100%-64px)] w-[300px] md:w-[375px] z-40 md:z-20 transition-right duration-300 inset-y-0 lg:top-auto lg:bottom-auto',
            showDetails ? 'visible right-0' : 'invisible right-[-375px]'
          )}>
          <TitleDetails title={title} onClose={() => setShowDetails(false)} />
        </div>
        <button
          onClick={() => setShowDetails(true)}
          className="absolute top-0 right-0 z-10 p-5 font-medium text-md">
          <i className="pr-1 align-middle nf-icon nf-details text-h3" />
          {t('section.scoreDetails')}
        </button>
      </div>
    </PageLayout>
  )
}

export default TitleDetailPage
